if (!customElements.get('product-carousel')) {
    customElements.define(
        'product-carousel',
        class ProductCarousel extends HTMLElement {
            connectedCallback() {
                this.track = this.querySelector('.product-carousel__track');
                this.slides = Array.from(this.querySelectorAll('.product-carousel__slide'));
                this.dotsWrap = this.querySelector('.product-carousel__dots');

                // Settings from data attributes (set by Liquid schema)
                this.autoplay = this.dataset.autoplay !== 'false';
                this.speed = parseInt(this.dataset.speed, 10) || 4;
                this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
                this.pauseOnFocus = this.dataset.pauseOnFocus !== 'false';
                this.restartAfterPause = this.dataset.restartAfterPause !== 'false';
                this.restartDelay = (parseInt(this.dataset.restartDelay, 10) || 0) * 1000;

                this.paused = false;
                this._restartTimer = null;

                if (!this.slides.length) return;

                this._calcVisible();
                this._duplicate();
                this._setAnimation();
                this._buildDots();
                this._bindEvents();
            }

            disconnectedCallback() {
                window.removeEventListener('resize', this._onResize);
                clearTimeout(this._restartTimer);
                clearTimeout(this._dotTimer);
            }

            // ── Setup ────────────────────────────────────────────

            _calcVisible() {
                const w = window.innerWidth;
                const desktopCols = parseInt(
                    getComputedStyle(document.documentElement).getPropertyValue('--carousel-columns') || 4, 10
                );
                this.visible = w >= 990 ? desktopCols : w >= 750 ? 2 : 1;
            }

            _duplicate() {
                this.track.querySelectorAll('.is-clone').forEach(el => el.remove());
                this.slides.forEach(slide => {
                    const clone = slide.cloneNode(true);
                    clone.classList.add('is-clone');
                    clone.setAttribute('aria-hidden', 'true');
                    this.track.appendChild(clone);
                });
                this.allItems = Array.from(this.track.children);
                this._setSlideSizes();
            }

            _setSlideSizes() {
                const total = this.allItems.length;
                this.track.style.width = `${(total / (this.visible + 1.6)) * 100}%`;
                this.allItems.forEach(el => { el.style.width = `${100 / total}%`; });
            }

            _setAnimation() {
                if (!this.autoplay) {
                    this.track.style.animationName = 'none';
                    return;
                }
                const duration = this.speed * this.slides.length;
                this.track.style.animationDuration = `${duration}s`;
                this.track.style.animationName = 'carousel-scroll';
                this.track.style.animationPlayState = 'running';
            }

            // ── Pause / Resume ───────────────────────────────────

            _pause() {
                if (!this.autoplay || this.paused) return;
                this.paused = true;
                clearTimeout(this._restartTimer);
                this.track.style.animationPlayState = 'paused';
            }

            _resume(withDelay = false) {
                if (!this.autoplay) return;
                clearTimeout(this._restartTimer);
                const doResume = () => {
                    this.paused = false;
                    this.track.style.animationPlayState = 'running';
                };
                if (withDelay && this.restartAfterPause && this.restartDelay > 0) {
                    this._restartTimer = setTimeout(doResume, this.restartDelay);
                } else if (!withDelay || this.restartAfterPause) {
                    doResume();
                }
                // If restartAfterPause is false and withDelay is true, stay paused permanently
            }

            // ── Dots ─────────────────────────────────────────────

            _buildDots() {
                if (!this.dotsWrap) return;
                this.dotsWrap.innerHTML = '';
                this.slides.forEach((_, i) => {
                    const btn = document.createElement('button');
                    btn.className = 'product-carousel__dot' + (i === 0 ? ' is-active' : '');
                    btn.setAttribute('aria-label', `Slide ${i + 1}`);
                    btn.setAttribute('tabindex', '-1');
                    this.dotsWrap.appendChild(btn);
                });
                if (this.autoplay) this._animateDots();
            }

            _animateDots() {
                const dots = Array.from(this.dotsWrap.querySelectorAll('.product-carousel__dot'));
                const n = this.slides.length;
                const perSlide = this.speed * 1000;
                let idx = 0;
                const tick = () => {
                    if (!this.paused) {
                        idx = (idx + 1) % n;
                        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
                    }
                    this._dotTimer = setTimeout(tick, perSlide);
                };
                clearTimeout(this._dotTimer);
                this._dotTimer = setTimeout(tick, perSlide);
            }

            // ── Events ───────────────────────────────────────────

            _bindEvents() {
                const container = this.querySelector('.product-carousel') || this;

                if (this.pauseOnHover) {
                    container.addEventListener('mouseenter', () => this._pause());
                    container.addEventListener('mouseleave', () => this._resume(true));
                }

                if (this.pauseOnFocus) {
                    container.addEventListener('focusin', () => this._pause());
                    container.addEventListener('focusout', () => this._resume(true));
                }

                this._onResize = () => {
                    const prev = this.visible;
                    this._calcVisible();
                    if (this.visible !== prev) {
                        this._duplicate();
                        this._setAnimation();
                    }
                };
                window.addEventListener('resize', this._onResize);

                document.addEventListener('shopify:section:load', e => {
                    if (e.target.contains(this)) {
                        clearTimeout(this._dotTimer);
                        clearTimeout(this._restartTimer);
                        this.connectedCallback();
                    }
                });
            }
        }


    );
}
